import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  LuBell,
  LuChevronDown,
  LuChevronLeft,
  LuChevronRight,
  LuLayoutDashboard,
  LuLogOut,
  LuSearch,
  LuSettings,
  LuUser,
} from 'react-icons/lu';
import { adminResources } from '../config/resources';
import { api } from '../api';
import {
  adminNavigation,
  adminPortfolioNavigation,
  adminSettingsNavigation,
  adminStandaloneItems,
  buildModulePath,
  createPortfolioNavItem,
  getPageTitle,
  projectMatchesSlug,
} from '../config/navigation';
import { resolveLinkedProject } from '../../utils/portfolioMatching';
import { useAdminAuth } from '../context/AdminAuthContext';
import AdminThemeToggle from './AdminThemeToggle';

const resourceKeys = new Set(adminResources.map((item) => item.key));
const SIDEBAR_STORAGE_KEY = 'admin_sidebar_collapsed';

function getInitials(name = '') {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'RT';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function isPathActive(path, location) {
  return `${location.pathname}${location.search}` === path;
}

function Tooltip({ label, show }) {
  if (!show || !label) return null;
  return (
    <span className="pointer-events-none absolute left-[calc(100%+10px)] top-1/2 z-50 -translate-y-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2.5 py-1.5 text-xs font-semibold text-white shadow-md dark:bg-white dark:text-slate-900">
      {label}
    </span>
  );
}

const AdminLayout = () => {
  const { user, logout } = useAdminAuth();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true',
  );
  const [openGroups, setOpenGroups] = useState(() => ({
    ...adminNavigation.reduce((acc, group) => {
      acc[group.key] = group.defaultOpen !== false;
      return acc;
    }, {}),
    [adminPortfolioNavigation.key]: adminPortfolioNavigation.defaultOpen !== false,
    [adminSettingsNavigation.key]: adminSettingsNavigation.defaultOpen !== false,
  }));
  const [hoveredKey, setHoveredKey] = useState(null);
  const [portfolioProjects, setPortfolioProjects] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [recentMessages, setRecentMessages] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadPortfolioProjects = async () => {
      try {
        const [overviewResponse, projectResponse] = await Promise.all([
          api.list('/api/portfolio-overviews'),
          api.list('/api/projects'),
        ]);
        if (!isMounted) return;

        const overviews = (overviewResponse.data || [])
          .filter((item) => item.isActive !== false)
          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
          .slice(0, 3);

        const projects = (projectResponse.data || []).filter((item) => item.isActive !== false);

        const linkedProjects = overviews
          .map((overview) => resolveLinkedProject(overview, projects))
          .filter(Boolean);

        setPortfolioProjects(linkedProjects);
      } catch {
        if (isMounted) setPortfolioProjects([]);
      }
    };

    loadPortfolioProjects();
    return () => {
      isMounted = false;
    };
  }, [location.pathname, location.search]);

  useEffect(() => {
    let isMounted = true;

    const loadUnreadMessages = async () => {
      try {
        const response = await api.list('/api/messages');
        if (!isMounted) return;
        const items = response.data || [];
        const unread = items.filter((item) => !item.isRead).length;
        setUnreadMessages(unread);
        setRecentMessages(
          [...items]
            .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
            .slice(0, 5),
        );
      } catch {
        if (isMounted) {
          setUnreadMessages(0);
          setRecentMessages([]);
        }
      }
    };

    loadUnreadMessages();
    return () => {
      isMounted = false;
    };
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
  };

  const toggleGroup = (groupKey) => {
    setOpenGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const pageTitle = useMemo(() => {
    if (location.pathname === '/admin') return 'Dashboard';

    const params = new URLSearchParams(location.search);
    const slug = params.get('slug');
    if (location.pathname.includes('/projects') && slug && params.get('featured') !== '1') {
      const project = portfolioProjects.find((item) => projectMatchesSlug(item, slug));
      if (project?.title) return project.title;
    }

    return getPageTitle(location.pathname, location.search);
  }, [location.pathname, location.search, portfolioProjects]);

  const userInitials = getInitials(user?.fullName || 'RAADSO TECH Admin');

  const portfolioMenuItems = useMemo(() => {
    return portfolioProjects.map((project, index) => ({
      ...createPortfolioNavItem(project),
      navKey: `portfolio-${project.id || project.slug || index}-${index}`,
    }));
  }, [portfolioProjects]);

  const standaloneMenuItems = useMemo(
    () =>
      adminStandaloneItems
        .filter((item) => resourceKeys.has(item.key))
        .map((item, index) => ({
          ...item,
          navKey: `standalone-${item.label}-${index}`,
          badge: item.key === 'messages' ? unreadMessages : 0,
        })),
    [unreadMessages],
  );

  const settingsMenuItems = useMemo(
    () =>
      adminSettingsNavigation.items
        .filter(
          (item) =>
            item.key === 'profile-settings'
            || item.key === 'website-sections'
            || resourceKeys.has(item.key),
        )
        .map((item, index) => ({
          ...item,
          navKey: `settings-${item.label}-${index}`,
        })),
    [],
  );

  const allMenuItems = useMemo(() => {
    const grouped = adminNavigation.flatMap((group) =>
      group.items
        .filter((item) => resourceKeys.has(item.key))
        .map((item, index) => ({
          ...item,
          navKey: `${group.key}-${item.label}-${index}`,
        })),
    );

    return [...grouped, ...standaloneMenuItems, ...portfolioMenuItems, ...settingsMenuItems];
  }, [portfolioMenuItems, standaloneMenuItems, settingsMenuItems]);

  const sidebarLinkClass = (isActive) =>
    [
      'group mx-2 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200',
      isActive
        ? 'bg-gradient-to-r from-primary to-[#e6bb12] text-white shadow-[0_4px_14px_rgba(243,199,21,0.35)]'
        : 'text-slate-600 hover:bg-primary/10 hover:text-slate-900 dark:text-gray-300 dark:hover:bg-primary/10 dark:hover:text-white',
    ].join(' ');

  const sidebarIconClass = (isActive) =>
    isActive ? 'text-white' : 'text-slate-500 group-hover:text-slate-700 dark:text-gray-400';

  const renderSidebarLink = (item) => {
    const Icon = item.icon;
    const path = item.to || buildModulePath(item);
    const active = Boolean(item.end)
      ? location.pathname === path.split('?')[0]
      : isPathActive(path, location);

    return (
      <NavLink
        key={item.navKey}
        to={path}
        end={item.end}
        className={sidebarLinkClass(active)}
      >
        <Icon size={18} strokeWidth={1.75} className={sidebarIconClass(active)} />
        <span className="truncate">{item.label}</span>
        {item.badge > 0 ? (
          <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-bold text-black">
            {item.badge > 9 ? '9+' : item.badge}
          </span>
        ) : null}
      </NavLink>
    );
  };

  const renderGroupToggle = (groupKey, title, Icon) => {
    const isOpen = openGroups[groupKey];
    return (
      <button
        type="button"
        onClick={() => toggleGroup(groupKey)}
        className="flex w-full items-center justify-between rounded-lg px-5 py-2 text-left text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 transition-colors hover:text-slate-600 dark:text-gray-500 dark:hover:text-gray-300"
      >
        <span className="inline-flex items-center gap-2">
          {Icon ? <Icon size={13} strokeWidth={2} /> : null}
          {title}
        </span>
        {isOpen ? <LuChevronDown size={14} /> : <LuChevronRight size={14} />}
      </button>
    );
  };

  const renderNavGroupItems = (items) => (
    <div className="space-y-0.5">{items.map((item) => renderSidebarLink(item))}</div>
  );

  const collapsedIconClass = (isActive) =>
    [
      'group relative mx-auto flex h-10 w-10 items-center justify-center rounded-xl transition-colors',
      isActive
        ? 'bg-gradient-to-r from-primary to-[#e6bb12] text-white shadow-sm'
        : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white',
    ].join(' ');

  const renderCollapsedIconLink = (to, label, icon, navKey, end = false) => {
    const Icon = icon;
    const active = end ? location.pathname === '/admin' : isPathActive(to, location);

    return (
      <NavLink
        key={navKey}
        to={to}
        end={end}
        className={collapsedIconClass(active)}
        onMouseEnter={() => setHoveredKey(navKey)}
        onMouseLeave={() => setHoveredKey(null)}
      >
        <Icon size={20} strokeWidth={1.75} />
        <Tooltip label={label} show={hoveredKey === navKey} />
      </NavLink>
    );
  };

  const collapsedNavItems = useMemo(() => allMenuItems, [allMenuItems]);

  const renderCollapsedAction = (label, icon, navKey, onClick) => {
    const Icon = icon;
    return (
      <button
        key={navKey}
        type="button"
        onClick={onClick}
        className={collapsedIconClass(false)}
        onMouseEnter={() => setHoveredKey(navKey)}
        onMouseLeave={() => setHoveredKey(null)}
      >
        <Icon size={20} strokeWidth={1.75} />
        <Tooltip label={label} show={hoveredKey === navKey} />
      </button>
    );
  };

  return (
    <div className="flex h-screen w-full overflow-hidden bg-[#f3f5f9] text-slate-900 transition-colors dark:bg-[#070b12] dark:text-white">
      <aside
        className={[
          'relative flex h-full shrink-0 flex-col overflow-hidden border-r border-slate-200/80 bg-white transition-[width] duration-300 dark:border-white/10 dark:bg-[#0f1319]',
          collapsed ? 'w-[72px]' : 'w-[280px]',
        ].join(' ')}
      >
          {collapsed ? (
            <>
              <div className="flex flex-col items-center gap-2 border-b border-slate-100 px-2 py-3 dark:border-white/10">
                <Link to="/admin" className="flex items-center justify-center">
                  <img
                    src="/img/logo-black.png"
                    alt="RAADSO Tech"
                    className="h-8 w-8 object-contain dark:hidden"
                  />
                  <img
                    src="/img/logo-white.png"
                    alt="RAADSO Tech"
                    className="hidden h-8 w-8 object-contain dark:block"
                  />
                </Link>
                <button
                  type="button"
                  onClick={() => setCollapsed(false)}
                  aria-label="Expand sidebar"
                  className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/10 dark:hover:text-white"
                >
                  <LuChevronRight size={18} strokeWidth={2} />
                </button>
              </div>

              <nav className="flex flex-1 flex-col overflow-y-auto px-2 py-3">
                <div className="flex flex-col items-center gap-1">
                  {renderCollapsedIconLink('/admin', 'Dashboard', LuLayoutDashboard, 'dashboard', true)}

                  {collapsedNavItems.map((item) => {
                    const Icon = item.icon;
                    const path = item.to || buildModulePath(item);
                    const active = item.end
                      ? location.pathname === '/admin'
                      : isPathActive(path, location);

                    return (
                      <NavLink
                        key={item.navKey}
                        to={path}
                        end={item.end}
                        className={collapsedIconClass(active)}
                        onMouseEnter={() => setHoveredKey(item.navKey)}
                        onMouseLeave={() => setHoveredKey(null)}
                      >
                        <Icon size={20} strokeWidth={1.75} />
                        <Tooltip label={item.label} show={hoveredKey === item.navKey} />
                      </NavLink>
                    );
                  })}
                </div>

                <div className="mt-auto flex flex-col items-center gap-1 border-t border-slate-100 pt-3 dark:border-white/10">
                  {renderCollapsedAction('Log out', LuLogOut, 'logout', handleLogout)}
                </div>
              </nav>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setCollapsed(true)}
                aria-label="Collapse sidebar"
                className="absolute right-3 top-5 z-30 flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 transition-colors hover:text-slate-700 dark:border-white/15 dark:bg-[#171c26] dark:text-gray-300 dark:hover:text-white"
              >
                <LuChevronLeft size={16} strokeWidth={2} />
              </button>

              <div className="border-b border-slate-100 px-5 pb-5 pt-5 dark:border-white/10">
                <Link to="/admin" className="inline-flex items-center">
                  <img
                    src="/img/logo-black.png"
                    alt="RAADSO Tech"
                    className="h-10 w-auto max-w-[210px] object-contain object-left dark:hidden"
                  />
                  <img
                    src="/img/logo-white.png"
                    alt="RAADSO Tech"
                    className="hidden h-10 w-auto max-w-[210px] object-contain object-left dark:block"
                  />
                </Link>
              </div>

              <nav className="flex-1 overflow-y-auto overflow-x-hidden pb-2">
                <p className="px-5 pb-2 pt-3 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 dark:text-gray-500">
                  My Panel
                </p>
                <div className="space-y-0.5">
                  <NavLink
                    to="/admin"
                    end
                    className={({ isActive }) => sidebarLinkClass(isActive)}
                  >
                    {({ isActive }) => (
                      <>
                        <LuLayoutDashboard size={18} strokeWidth={1.75} className={sidebarIconClass(isActive)} />
                        <span className="truncate">Dashboard</span>
                      </>
                    )}
                  </NavLink>
                </div>

                <div className="mx-4 my-3 border-t border-slate-100 dark:border-white/10" />

                {adminNavigation.map((group) => {
                  const GroupIcon = group.icon;
                  const visibleItems = group.items
                    .filter((item) => resourceKeys.has(item.key))
                    .map((item, index) => ({
                      ...item,
                      navKey: `${group.key}-${item.label}-${index}`,
                    }));

                  if (visibleItems.length === 0) return null;

                  return (
                    <div key={group.key}>
                      {renderGroupToggle(group.key, group.title, GroupIcon)}
                      {openGroups[group.key] ? renderNavGroupItems(visibleItems) : null}
                    </div>
                  );
                })}

                <div className="mx-4 my-3 border-t border-slate-100 dark:border-white/10" />

                <div>
                  {renderGroupToggle(
                    adminPortfolioNavigation.key,
                    adminPortfolioNavigation.title,
                    adminPortfolioNavigation.icon,
                  )}
                  {openGroups[adminPortfolioNavigation.key] ? (
                    portfolioMenuItems.length > 0 ? (
                      renderNavGroupItems(portfolioMenuItems)
                    ) : (
                      <p className="px-5 py-2 text-xs text-slate-400">No projects yet</p>
                    )
                  ) : null}
                </div>

                {standaloneMenuItems.length > 0 ? (
                  <>
                    <div className="mx-4 my-3 border-t border-slate-100 dark:border-white/10" />
                    {renderNavGroupItems(standaloneMenuItems)}
                  </>
                ) : null}

                <div className="mx-4 my-3 border-t border-slate-100 dark:border-white/10" />

                <div>
                  {renderGroupToggle(
                    adminSettingsNavigation.key,
                    adminSettingsNavigation.title,
                    adminSettingsNavigation.icon,
                  )}
                  {openGroups[adminSettingsNavigation.key] ? renderNavGroupItems(settingsMenuItems) : null}
                </div>
              </nav>

              <div className="border-t border-slate-100 px-3 py-4 dark:border-white/10">
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200 dark:bg-white/5 dark:text-gray-200 dark:hover:bg-white/10"
                >
                  <LuLogOut size={16} strokeWidth={1.75} />
                  Logout
                </button>
              </div>
            </>
          )}
        </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="sticky top-0 z-30 shrink-0 border-b border-slate-200/70 bg-white px-4 py-3 dark:border-white/10 dark:bg-[#0a0d14] md:px-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex min-w-0 flex-1 items-center gap-3 rounded-full border border-slate-200/70 bg-slate-50 px-4 py-2.5 text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-gray-300 lg:max-w-2xl">
              <LuSearch size={18} strokeWidth={1.75} className="shrink-0 text-slate-400 dark:text-gray-500" />
              <input
                placeholder="Search anything..."
                className="min-w-0 w-full bg-transparent text-sm outline-none placeholder:text-slate-400 dark:placeholder:text-gray-500"
              />
            </div>

            <div className="flex shrink-0 items-center gap-2.5 md:gap-3">
              <AdminThemeToggle />

              <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    setNotificationsOpen((prev) => !prev);
                    setProfileOpen(false);
                  }}
                  className={[
                    'relative inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200/70 bg-slate-50 text-slate-600 transition-colors hover:text-slate-900 dark:border-white/10 dark:bg-white/5 dark:text-gray-200 dark:hover:text-white',
                    notificationsOpen ? 'ring-2 ring-primary/25' : '',
                  ].join(' ')}
                  aria-label="Notifications"
                  aria-expanded={notificationsOpen}
                  aria-haspopup="menu"
                >
                  <LuBell size={18} strokeWidth={1.75} />
                  {unreadMessages > 0 ? (
                    <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none text-black ring-2 ring-white dark:ring-[#0a0d14]">
                      {unreadMessages > 9 ? '9+' : unreadMessages}
                    </span>
                  ) : null}
                </button>

                {notificationsOpen ? (
                  <>
                    <button
                      type="button"
                      className="fixed inset-0 z-40 cursor-default bg-transparent"
                      aria-label="Close notifications"
                      onClick={() => setNotificationsOpen(false)}
                    />
                    <div
                      role="menu"
                      className="absolute right-0 z-50 mt-3 w-[340px] origin-top-right overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_20px_50px_rgba(15,23,42,0.14)] dark:border-white/10 dark:bg-[#121820]"
                    >
                      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-white/10">
                        <div>
                          <p className="text-sm font-semibold text-slate-900 dark:text-white">Messages</p>
                          <p className="text-xs text-slate-500 dark:text-gray-400">
                            {unreadMessages > 0 ? `${unreadMessages} new` : 'No new messages'}
                          </p>
                        </div>
                        <Link
                          to="/admin/modules/messages"
                          onClick={() => setNotificationsOpen(false)}
                          className="text-xs font-semibold text-primary hover:underline"
                        >
                          View all
                        </Link>
                      </div>

                      <div className="max-h-80 overflow-y-auto p-2">
                        {recentMessages.length > 0 ? (
                          recentMessages.map((item) => {
                            const name = [item.firstName, item.lastName].filter(Boolean).join(' ') || 'Unknown';
                            const isRead = Boolean(item.isRead);
                            return (
                              <Link
                                key={item.id}
                                to="/admin/modules/messages"
                                role="menuitem"
                                onClick={() => setNotificationsOpen(false)}
                                className="flex items-start gap-3 rounded-xl px-3 py-2.5 transition hover:bg-slate-50 dark:hover:bg-white/5"
                              >
                                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[10px] font-bold text-slate-800 dark:text-primary">
                                  {getInitials(name)}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center justify-between gap-2">
                                    <p className="truncate text-sm font-medium text-slate-900 dark:text-white">
                                      {name}
                                    </p>
                                    <span
                                      className={[
                                        'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold',
                                        isRead
                                          ? 'bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-gray-400'
                                          : 'bg-primary/20 text-slate-900 dark:text-primary',
                                      ].join(' ')}
                                    >
                                      {isRead ? 'Read' : 'New'}
                                    </span>
                                  </div>
                                  <p className="truncate text-xs text-slate-500 dark:text-gray-400">
                                    {item.subject || 'No subject'}
                                  </p>
                                </div>
                              </Link>
                            );
                          })
                        ) : (
                          <p className="px-3 py-8 text-center text-sm text-slate-500 dark:text-gray-400">
                            No messages yet.
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                ) : null}
              </div>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    setProfileOpen((prev) => !prev);
                    setNotificationsOpen(false);
                  }}
                  className={[
                    'flex items-center gap-2.5 rounded-full border border-slate-200/70 bg-slate-50 py-1.5 pl-1.5 pr-3 transition-all dark:border-white/10 dark:bg-white/5',
                    profileOpen ? 'ring-2 ring-primary/25' : 'hover:border-slate-300 dark:hover:border-white/20',
                  ].join(' ')}
                  aria-expanded={profileOpen}
                  aria-haspopup="menu"
                >
                  {user?.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt={user?.fullName || 'Admin'}
                      className="h-9 w-9 rounded-full object-cover"
                    />
                  ) : (
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-xs font-extrabold text-black">
                      {userInitials}
                    </div>
                  )}
                  <div className="hidden text-left leading-tight sm:block">
                    <p className="text-sm font-bold text-slate-900 dark:text-white">
                      {user?.fullName || 'RAADSO TECH Admin'}
                    </p>
                    <p className="text-[11px] text-slate-500 dark:text-gray-400">Super Admin</p>
                  </div>
                  <LuChevronDown
                    size={16}
                    strokeWidth={2}
                    className={[
                      'hidden text-slate-500 transition-transform duration-200 sm:block dark:text-gray-400',
                      profileOpen ? 'rotate-180' : '',
                    ].join(' ')}
                  />
                </button>

                {profileOpen ? (
                  <>
                    <button
                      type="button"
                      className="fixed inset-0 z-40 cursor-default bg-transparent"
                      aria-label="Close profile menu"
                      onClick={() => setProfileOpen(false)}
                    />
                    <div
                      role="menu"
                      className="absolute right-0 z-50 mt-3 w-56 origin-top-right overflow-hidden rounded-2xl border border-slate-100 bg-white p-2 shadow-[0_20px_50px_rgba(15,23,42,0.14)] dark:border-white/10 dark:bg-[#121820]"
                    >
                      <div className="mb-1 border-b border-slate-100 px-3 py-2.5 dark:border-white/10">
                        <p className="text-sm font-bold text-slate-900 dark:text-white">
                          {user?.fullName || 'RAADSO TECH Admin'}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-gray-400">{user?.email || 'admin@raadsotech.com'}</p>
                      </div>
                      <Link
                        to="/admin/modules/profile-settings"
                        role="menuitem"
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:text-gray-200 dark:hover:bg-white/5"
                      >
                        <LuUser size={16} strokeWidth={1.75} className="text-slate-400" />
                        Profile Settings
                      </Link>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setProfileOpen(false);
                          handleLogout();
                        }}
                        className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-red-500 transition hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10"
                      >
                        <LuLogOut size={16} strokeWidth={1.75} />
                        Logout
                      </button>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </header>

        <main className="min-w-0 flex-1 overflow-x-hidden overflow-y-auto bg-[#f3f5f9] p-4 dark:bg-[#0a0d14] md:p-6">
          {location.pathname === '/admin' ? null : (
            <div className="mb-4 rounded-2xl border border-primary/20 bg-primary/5 px-4 py-2.5 text-xs text-slate-600 dark:border-primary/20 dark:bg-primary/10 dark:text-gray-300">
              <span className="font-bold text-slate-900 dark:text-white">{pageTitle}</span>
            </div>
          )}

          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
